import React, { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import './ToolPage.css'
import './BingoRoom.css'
import ConfirmModal from './ConfirmModal'
import {
  drawBingoNumber,
  undrawBingoNumber,
  removeBingoParticipant,
  fetchBingoState,
  bingoStreamUrl,
  bingoJoinUrl,
} from './utils/bingoApiClient'
import { trackEvent } from '../../lib/analytics'
import { useDocumentMeta } from '../../hooks/useDocumentMeta'

const ALL_NUMBERS = Array.from({ length: 75 }, (_, i) => i + 1)
const COLUMN_LABELS = ['B', 'I', 'N', 'G', 'O']
const WIN_CONDITION_LABELS = {
  full_card: 'Cartela cheia',
  line: 'Linha (horizontal)',
  column: 'Coluna (vertical)',
  diagonal: 'Diagonal',
}

const BingoRoomMaster = () => {
  useDocumentMeta({
    title: 'Bingo ao Vivo, Mestre da Sala | jarder.dev',
    description: 'Sorteie os números e acompanhe ao vivo quem está perto de ganhar.',
  })

  const { id } = useParams()
  const masterToken = (/token=([^&]+)/.exec(window.location.hash) || [])[1] || ''

  const [state, setState] = useState(null)
  const [status, setStatus] = useState('loading')
  const [error, setError] = useState('')
  const [copied, setCopied] = useState(false)
  const [pendingConfirm, setPendingConfirm] = useState(null)

  const joinUrl = bingoJoinUrl(id)

  useEffect(() => {
    let cancelled = false

    fetchBingoState(id)
      .then((data) => {
        if (cancelled) return
        setState(data)
        setStatus('ready')
      })
      .catch(() => {
        if (!cancelled) setStatus('notfound')
      })

    const eventSource = new EventSource(bingoStreamUrl(id))
    eventSource.onmessage = (event) => {
      setState(JSON.parse(event.data))
    }

    return () => {
      cancelled = true
      eventSource.close()
    }
  }, [id])

  const draw = async (number) => {
    setError('')
    try {
      await drawBingoNumber(id, masterToken, number)
      trackEvent('tool_generate', { tool: 'bingo_room_draw', manual: number !== undefined })
    } catch (err) {
      setError(err.message || 'Não foi possível sortear. Confira se você está na tela de mestre certa.')
    }
  }

  const undraw = async (number) => {
    setError('')
    try {
      await undrawBingoNumber(id, masterToken, number)
      trackEvent('tool_generate', { tool: 'bingo_room_undraw' })
    } catch (err) {
      setError(err.message || 'Não foi possível desmarcar o número.')
    }
  }

  const toggleNumber = (number, alreadyDrawn) => {
    if (!alreadyDrawn) {
      draw(number)
      return
    }
    setPendingConfirm({
      message: `Desmarcar o número ${number}?`,
      confirmLabel: 'Desmarcar',
      onConfirm: () => undraw(number),
    })
  }

  const removeParticipant = async (participantId) => {
    setError('')
    try {
      await removeBingoParticipant(id, masterToken, participantId)
      trackEvent('tool_generate', { tool: 'bingo_room_remove_participant' })
    } catch (err) {
      setError(err.message || 'Não foi possível excluir o participante.')
    }
  }

  const confirmRemoveParticipant = (participantId, name) => {
    setPendingConfirm({
      message: `Excluir ${name} da sala?`,
      confirmLabel: 'Excluir',
      onConfirm: () => removeParticipant(participantId),
    })
  }

  const copyJoinLink = () => {
    navigator.clipboard.writeText(joinUrl)
    setCopied(true)
    trackEvent('tool_copy', { tool: 'bingo_room' })
  }

  if (status === 'loading') {
    return (
      <div className="tool-page">
        <p>Carregando...</p>
      </div>
    )
  }

  if (status === 'notfound') {
    return (
      <div className="tool-page">
        <Link to="/ferramentas" className="tool-back-link">
          ← Todas as ferramentas
        </Link>
        <h1>Sala não encontrada</h1>
        <p className="tool-disclaimer">Essa sala não existe ou expirou.</p>
        <Link to="/ferramentas/bingo-ao-vivo" className="tool-button">
          Criar uma nova sala
        </Link>
      </div>
    )
  }

  const drawnSet = new Set(state.drawnNumbers)
  const lastDrawn = state.drawnNumbers[state.drawnNumbers.length - 1]
  const winConditionSummary = state.winConditions.map((key) => WIN_CONDITION_LABELS[key]).join(' ou ')

  return (
    <div className="tool-page">
      <Link to="/ferramentas" className="tool-back-link">
        ← Todas as ferramentas
      </Link>
      <h1>Bingo ao Vivo, Mestre da Sala</h1>
      <p className="tool-disclaimer">
        Essa tela é só sua. Compartilhe o link abaixo com os
        participantes: cada um que entrar recebe uma cartela própria.
        Condição de vitória: <strong>{winConditionSummary}</strong>.
      </p>

      <div className="tool-result-card">
        <span className="tool-result-label">Link pra participantes</span>
        <span className="tool-result-value" style={{ wordBreak: 'break-all' }}>
          {joinUrl}
        </span>
        <div className="tool-result-actions">
          <button type="button" className="tool-button tool-button-secondary" onClick={copyJoinLink}>
            {copied ? 'Copiado!' : 'Copiar link'}
          </button>
        </div>
      </div>

      <div className="bingo-draw-section">
        <button
          type="button"
          className="tool-button"
          onClick={() => draw()}
          disabled={state.drawnNumbers.length >= 75}
        >
          {state.drawnNumbers.length >= 75 ? 'Todos os números sorteados' : 'Sortear número aleatório'}
        </button>
        {lastDrawn && <span className="bingo-last-drawn">Último: {lastDrawn}</span>}
      </div>

      {error && <p className="tool-error">{error}</p>}

      <p className="tool-import-hint">
        Usando um sorteador físico? Clique no número que saiu direto no
        quadro abaixo. Clique de novo pra desmarcar, caso tenha errado.
      </p>

      <div className="bingo-board">
        {COLUMN_LABELS.map((label, columnIndex) => (
          <div className="bingo-board-column" key={label}>
            <span className="bingo-board-letter">{label}</span>
            {ALL_NUMBERS.slice(columnIndex * 15, columnIndex * 15 + 15).map((number) => {
              const isDrawn = drawnSet.has(number)
              return (
                <button
                  key={number}
                  type="button"
                  onClick={() => toggleNumber(number, isDrawn)}
                  className={`bingo-board-number${isDrawn ? ' drawn' : ''}`}
                >
                  {number}
                </button>
              )
            })}
          </div>
        ))}
      </div>

      <h2 className="bingo-participants-heading">
        Participantes ({state.participants.length})
      </h2>

      {state.participants.length === 0 ? (
        <p className="tool-import-hint">
          Ninguém entrou ainda. Compartilhe o link acima.
        </p>
      ) : (
        <ul className="bingo-participants-list">
          {state.participants.map((participant) => {
            let statusClass = ''
            if (participant.won) statusClass = ' won'
            else if (participant.remaining <= 2) statusClass = ' close'

            const showMissing = !participant.won && participant.remaining <= 2

            return (
              <li key={participant.id} className={`bingo-participant${statusClass}`}>
                <div className="bingo-participant-info">
                  <span className="bingo-participant-name">{participant.name}</span>
                  <span className="bingo-participant-status">
                    {participant.won ? '🏆 Bingo!' : `faltam ${participant.remaining}`}
                  </span>
                  {showMissing && (
                    <span className="bingo-participant-missing">
                      Faltam: {participant.missingNumbers.join(', ')}
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  className="bingo-participant-remove"
                  onClick={() => confirmRemoveParticipant(participant.id, participant.name)}
                  aria-label={`Excluir ${participant.name}`}
                >
                  Excluir
                </button>
              </li>
            )
          })}
        </ul>
      )}

      {pendingConfirm && (
        <ConfirmModal
          message={pendingConfirm.message}
          confirmLabel={pendingConfirm.confirmLabel}
          onConfirm={() => {
            pendingConfirm.onConfirm()
            setPendingConfirm(null)
          }}
          onCancel={() => setPendingConfirm(null)}
        />
      )}
    </div>
  )
}

export default BingoRoomMaster
